import { Button, Card, Heading, LuIcon, Separator, Text, toast } from "@overmap-ai/blocks"
import { type Form, type Stored, EMPTY_ARRAY, ERROR_MSG_UNEXPECTED } from "@overmap-ai/common"
import { APIError, selectFormById, selectFormsOfOrganization, selectLatestFormRevisionOfForm } from "@overmap-ai/core"
import { awaitFilesAndPromises, extractFilesAndPromisesFromFieldValues, type FieldValues } from "@overmap-ai/forms"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { FormDropdownSelect } from "../../components/DropdownSelects/FormDropdownSelect"
import { FormSubmitter } from "../../components/Form/FormSubmitter"
import { useDeserializeFieldSchema } from "../../components/Form/hooks"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/forms/$formId/submit")

export const SubmitFormView = memo(() => {
	const { formId, organizationId } = routeApi.useParams()

	const form = useAppSelector(selectFormById(formId))

	if (!form) {
		throw new Error("Expected form to exist.")
	}

	const latestFormRevision = useAppSelector(selectLatestFormRevisionOfForm(form.uuid))
	const forms = useShallowEqaulSelector(selectFormsOfOrganization(organizationId))
	const navigate = useNavigate()
	const fieldSchema = useDeserializeFieldSchema(latestFormRevision?.fields ?? EMPTY_ARRAY)

	const handleSubmit = useCallback(
		async (allValues: FieldValues) => {
			if (!latestFormRevision?.uuid) return

			const [values, files] = extractFilesAndPromisesFromFieldValues(fieldSchema.getFields(), allValues)

			const [formSubmission, formSubmissionPromise] = sdk.formSubmissions.add({
				form_revision: latestFormRevision.uuid,
				form: form.uuid,
				organization: organizationId,
				project: null,
				asset: null,
				issue: null,
			})

			const [formSubmissionFieldValues, formSubmissionFieldValuesPromise] = sdk.formSubmissionFieldValues.add({
				form_submission: formSubmission.uuid,
				values: fieldSchema.serializeValues(values),
				published_at: new Date().toISOString(),
			})

			if (Object.keys(files).length !== 0) {
				void sdk.formSubmissionFieldValuesAttachments.bulkAddForFormSubmissionFieldValues(
					formSubmissionFieldValues.uuid,
					await awaitFilesAndPromises(files),
				)
			}

			Promise.all([formSubmissionPromise, formSubmissionFieldValuesPromise])
				.then(() => {
					toast.success({
						title: "Success",
						description: "Succesfully submitted form.",
					})
				})
				.catch((error) => {
					toast.error({
						title: "Failed to submit form",
						description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
					})
				})

			void navigate({
				from: "/$organizationId/forms/$formId/submit",
				to: "/$organizationId/forms/submissions/$formSubmissionId",
				params: (prev) => ({ ...prev, formSubmissionId: formSubmission.uuid }),
			})
		},
		[latestFormRevision?.uuid, fieldSchema, navigate, form.uuid, organizationId],
	)

	const handleFormDropdownSelectValueChange = useCallback(
		(form: Stored<Form>) => {
			void navigate({
				from: "/$organizationId/forms/$formId/submit",
				to: "/$organizationId/forms/$formId/submit",
				replace: true,
				params: (prev) => ({
					...prev,
					formId: form.uuid,
				}),
				search: (prev) => prev,
			})
		},
		[navigate],
	)

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex h-full w-full flex-col gap-4 overflow-x-hidden overflow-y-scroll">
				<FormDropdownSelect
					required
					forms={forms}
					value={form}
					onValueChange={handleFormDropdownSelectValueChange}
					size="sm"
				>
					<Button variant="ghost" accentColor="base" className="w-max">
						<Heading size="xl">{form.name}</Heading>
						<LuIcon icon={modelIconMapping.chevronDown} />
					</Button>
				</FormDropdownSelect>

				<Separator size="full" />

				{latestFormRevision ? (
					<Card variant="soft" size="sm">
						<FormSubmitter formRevision={latestFormRevision} onSubmit={handleSubmit} size="sm" />
					</Card>
				) : (
					<Text size="sm" accentColor="base" weight="regular">
						No fields.
					</Text>
				)}
			</section>
		</ViewContainer>
	)
})
SubmitFormView.displayName = "SubmitFormView"
